/**
 * Collects browser metadata, console errors, and network failures
 * for inclusion in feedback submissions.
 */

interface CollectedMetadata {
  pageUrl: string;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  userAgent: string;
  browserMetadata: Record<string, unknown>;
  consoleErrors: string[];
  networkFailures: string[];
}

// Buffer to capture console errors
const consoleErrors: string[] = [];
const networkFailures: string[] = [];

// Intercept console.error
const originalError = console.error;
console.error = (...args: unknown[]) => {
  consoleErrors.push(
    args.map((a) => (typeof a === "string" ? a : JSON.stringify(a))).join(" ")
  );
  // Keep only the last 20 errors
  if (consoleErrors.length > 20) consoleErrors.shift();
  originalError.apply(console, args);
};

// Intercept window errors
window.addEventListener("error", (event) => {
  consoleErrors.push(
    `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`
  );
  if (consoleErrors.length > 20) consoleErrors.shift();
});

// Intercept unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  consoleErrors.push(`Unhandled rejection: ${event.reason}`);
  if (consoleErrors.length > 20) consoleErrors.shift();
});

// Intercept failed fetch/XHR requests
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  try {
    const response = await originalFetch.apply(window, args);
    if (!response.ok) {
      const url = typeof args[0] === "string" ? args[0] : (args[0] as Request).url;
      networkFailures.push(`${response.status} ${response.statusText}: ${url}`);
      if (networkFailures.length > 20) networkFailures.shift();
    }
    return response;
  } catch (err) {
    const url = typeof args[0] === "string" ? args[0] : (args[0] as Request).url;
    networkFailures.push(`Network error: ${url}`);
    if (networkFailures.length > 20) networkFailures.shift();
    throw err;
  }
};

function parseBrowserInfo(ua: string): Record<string, unknown> {
  const info: Record<string, unknown> = {};

  // Browser detection
  if (ua.includes("Chrome") && !ua.includes("Edg"))
    info.browser = "Chrome";
  else if (ua.includes("Firefox")) info.browser = "Firefox";
  else if (ua.includes("Safari") && !ua.includes("Chrome"))
    info.browser = "Safari";
  else if (ua.includes("Edg")) info.browser = "Edge";
  else info.browser = "Other";

  // OS detection
  if (ua.includes("Windows")) info.os = "Windows";
  else if (ua.includes("Mac OS")) info.os = "macOS";
  else if (ua.includes("Linux")) info.os = "Linux";
  else if (ua.includes("Android")) info.os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) info.os = "iOS";

  // Mobile detection
  info.mobile = /Mobi|Android/i.test(ua);

  return info;
}

export function collectMetadata(): CollectedMetadata {
  return {
    pageUrl: window.location.href,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
    userAgent: navigator.userAgent,
    browserMetadata: parseBrowserInfo(navigator.userAgent),
    consoleErrors: [...consoleErrors],
    networkFailures: [...networkFailures],
  };
}
